import { useLayoutEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useCreateCar, useUpdateCar } from '../../hooks/useApi';
import { errorMessage } from '../../services/api';
import { toast } from '../../store';
import type { Car, CarInput } from '../../types';
import { CAR_STATUS_LABELS, FUEL_LABELS, normalizeForm, toOptions } from '../../utils/helpers';
import { Button } from '../ui/Button';
import { Input, Select, Textarea } from '../ui/Input';
import { Modal } from '../ui/Modal';
import { t } from '../../i18n';

type FormValues = Record<keyof CarInput, string>;

const EMPTY: FormValues = {
  license_plate: '',
  brand: '',
  model: '',
  year: '',
  vin: '',
  fuel_type: '',
  status: 'active',
  current_km: '0',
  notes: '',
  official_consumption: '',
  tank_capacity: '',
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  car?: Car | null;
  onSaved?: (car: Car) => void;
}

export function CarFormModal({ isOpen, onClose, car, onSaved }: Props) {
  const create = useCreateCar();
  const update = useUpdateCar();
  const saving = create.isPending || update.isPending;
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ defaultValues: EMPTY });

  useLayoutEffect(() => {
    if (!isOpen) return;
    reset(
      car
        ? Object.fromEntries(Object.keys(EMPTY).map((k) => [k, String(car[k as keyof Car] ?? '')])) as FormValues
        : EMPTY,
    );
  }, [isOpen, car, reset]);

  const onSubmit = async (values: FormValues) => {
    const data = normalizeForm(values, ['year', 'current_km', 'official_consumption', 'tank_capacity']) as unknown as CarInput;
    data.current_km = data.current_km ?? 0;
    try {
      const saved = car ? await update.mutateAsync({ id: car.id, data }) : await create.mutateAsync(data);
      toast.success(car ? t('Vozidlo bolo uložené') : t('Vozidlo bolo vytvorené'));
      onSaved?.(saved);
      onClose();
    } catch (error) {
      toast.error(errorMessage(error, t('Vozidlo sa nepodarilo uložiť')));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      busy={saving}
      size="lg"
      title={car ? t('Upraviť vozidlo {plate}', { plate: car.license_plate }) : t('Nové vozidlo')}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t('Zrušiť')}
          </Button>
          <Button type="submit" form="car-form" loading={saving}>
            {car ? t('Uložiť') : t('Vytvoriť')}
          </Button>
        </>
      }
    >
      <form id="car-form" onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 gap-4 sm:grid-cols-2" noValidate>
        <Input
          label={t('ŠPZ')}
          required
          placeholder={t('BA-123AB')}
          className="uppercase"
          error={errors.license_plate?.message}
          {...register('license_plate', { required: t('ŠPZ je povinná'), maxLength: { value: 20, message: t('Max. 20 znakov') } })}
        />
        <Select label={t('Stav')} required options={toOptions(CAR_STATUS_LABELS)} {...register('status')} />
        <Input label={t('Značka')} required placeholder={t('Škoda')} error={errors.brand?.message} {...register('brand', { required: t('Značka je povinná') })} />
        <Input label={t('Model')} required placeholder={t('Octavia')} error={errors.model?.message} {...register('model', { required: t('Model je povinný') })} />
        <Input
          label={t('Rok výroby')}
          type="number"
          inputMode="numeric"
          error={errors.year?.message}
          {...register('year', {
            validate: (v) => !v || (Number(v) >= 1900 && Number(v) <= 2100) || t('Neplatný rok'),
          })}
        />
        <Select label={t('Palivo')} placeholder="–" options={toOptions(FUEL_LABELS)} {...register('fuel_type')} />
        <Input
          label={t('Stav tachometra (km)')}
          type="number"
          inputMode="numeric"
          min={0}
          error={errors.current_km?.message}
          {...register('current_km', { validate: (v) => !v || Number(v) >= 0 || t('Nesmie byť záporné') })}
        />
        <Input label="VIN" maxLength={50} className="uppercase" {...register('vin')} />
        <Input
          label={t('Kombinovaná spotreba podľa TP (l/100 km)')}
          inputMode="decimal"
          placeholder={t('napr. 6,2')}
          hint={t('Z technického preukazu – porovnáva sa so skutočným tankovaním')}
          error={errors.official_consumption?.message}
          {...register('official_consumption', {
            validate: (v) => !v || (Number(v.replace(',', '.')) > 0 && Number(v.replace(',', '.')) <= 50) || t('Zadajte číslo od 0 do 50'),
          })}
        />
        <Input
          label={t('Objem nádrže (l)')}
          inputMode="decimal"
          placeholder={t('napr. 50')}
          error={errors.tank_capacity?.message}
          {...register('tank_capacity', {
            validate: (v) => !v || (Number(v.replace(',', '.')) > 0 && Number(v.replace(',', '.')) <= 500) || t('Zadajte číslo od 0 do 500'),
          })}
        />
        <Textarea label={t('Poznámky')} wrapperClassName="sm:col-span-2" {...register('notes')} />
      </form>
    </Modal>
  );
}
